import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Input,
  Skeleton,
  Spinner
} from "@veritrellis/ui";
import { FileSearch, ScrollText, ShieldCheck } from "lucide-react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { AuditEventInspector } from "../components/audit/AuditEventInspector.js";
import { AuditEventTimeline } from "../components/audit/AuditEventTimeline.js";
import { apiFetch } from "../lib/api.js";
import {
  auditCategoryLabel,
  categorizeAuditEvent,
  eventMatchesSearch,
  type AuditCategory,
  type AuditEvent
} from "../lib/auditPresentation.js";
import { envLabel } from "../lib/requestPresentation.js";
import { usePersistedAdminEnvironment } from "../hooks/usePersistedAdminEnvironment.js";
import { useWorkspaceId } from "../hooks/useWorkspaceId.js";

type AuditPageResult = {
  events: AuditEvent[];
  nextCursor: string | null;
};

const PAGE_SIZE = 50;

const CATEGORY_FILTERS: AuditCategory[] = [
  "all",
  "authorization",
  "policy",
  "access",
  "security",
  "other"
];

export const AuditPage = () => {
  const { workspaceId, isLoading } = useWorkspaceId();
  const [environment] = usePersistedAdminEnvironment();
  const [search, setSearch] = useState("");
  const [requestIdFilter, setRequestIdFilter] = useState("");
  const [category, setCategory] = useState<AuditCategory>("all");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const q = useInfiniteQuery<AuditPageResult, Error>({
    queryKey: ["audit", workspaceId, environment, requestIdFilter.trim()],
    enabled: workspaceId !== null,
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams();
      params.set("environment", environment);
      params.set("limit", String(PAGE_SIZE));
      if (typeof pageParam === "string" && pageParam.length > 0) {
        params.set("cursor", pageParam);
      }
      const trimmed = requestIdFilter.trim();
      if (trimmed.length > 0) {
        params.set("request_id", trimmed);
      }
      const res = await apiFetch(`/admin/workspaces/${workspaceId}/audit?${params.toString()}`);
      if (!res.ok) {
        throw new Error(`audit_${String(res.status)}`);
      }
      const json = (await res.json()) as { events: AuditEvent[] };
      const nextCursor =
        json.events.length === PAGE_SIZE
          ? (json.events[json.events.length - 1]?.created_at ?? null)
          : null;
      return { events: json.events, nextCursor };
    }
  });

  const allEvents = useMemo(() => (q.data?.pages ?? []).flatMap((p) => p.events), [q.data]);

  const filtered = useMemo(() => {
    return allEvents.filter((e) => {
      if (category !== "all" && categorizeAuditEvent(e.event_type) !== category) {
        return false;
      }
      if (!eventMatchesSearch(e, search)) {
        return false;
      }
      return true;
    });
  }, [allEvents, category, search]);

  const categoryCounts = useMemo(() => {
    const counts: Record<Exclude<AuditCategory, "all">, number> = {
      authorization: 0,
      policy: 0,
      access: 0,
      security: 0,
      other: 0
    };
    for (const e of allEvents) {
      counts[categorizeAuditEvent(e.event_type)] += 1;
    }
    return counts;
  }, [allEvents]);

  const selectedEvent = useMemo(
    () => filtered.find((e) => e.id === selectedId) ?? filtered[0] ?? null,
    [filtered, selectedId]
  );

  useEffect(() => {
    if (filtered.length === 0) {
      setSelectedId(null);
      return;
    }
    if (selectedId === null || !filtered.some((e) => e.id === selectedId)) {
      setSelectedId(filtered[0]!.id);
    }
  }, [filtered, selectedId]);

  if (isLoading) {
    return (
      <div className="admin-page-stack">
        <Skeleton style={{ height: 48, width: "45%" }} />
        <Skeleton style={{ height: 400, width: "100%" }} />
      </div>
    );
  }
  if (workspaceId === null) {
    return <Navigate to="/onboarding" replace />;
  }

  return (
    <div className="admin-page-stack">
      <div className="admin-audit-compliance">
        <ShieldCheck
          style={{ width: 18, height: 18, flexShrink: 0, color: "#0d9488" }}
          aria-hidden
        />
        <div>
          <p className="admin-audit-compliance-title">Product audit lane</p>
          <p className="admin-form-field-hint">
            Veritrellis authorization and admin events only. IdP sign-in and directory audit remain
            in your identity provider — they are not merged into this stream.
          </p>
        </div>
      </div>

      <div className="admin-audit-workspace">
        <div className="admin-audit-main">
          <section className="admin-panel" aria-labelledby="audit-filters-heading">
            <div className="admin-panel-header">
              <h2 id="audit-filters-heading" className="admin-panel-title">
                Explore events
              </h2>
              <span style={{ fontSize: 12, color: "var(--muted-foreground)" }}>
                {envLabel(environment)}
              </span>
            </div>

            <div className="admin-filter-toolbar">
              <div className="admin-search-field" style={{ maxWidth: "100%", flex: "1 1 14rem" }}>
                <label className="admin-search-label" htmlFor="audit-search">
                  Search loaded events
                </label>
                <Input
                  id="audit-search"
                  type="search"
                  placeholder="Actor, event type, ID, payload…"
                  value={search}
                  onChange={(e) => setSearch(e.currentTarget.value)}
                />
              </div>
              <div className="admin-search-field" style={{ maxWidth: "100%", flex: "1 1 14rem" }}>
                <label className="admin-search-label" htmlFor="audit-request-id">
                  Request ID (server filter)
                </label>
                <Input
                  id="audit-request-id"
                  placeholder="req_…"
                  value={requestIdFilter}
                  onChange={(e) => setRequestIdFilter(e.currentTarget.value)}
                />
              </div>
            </div>

            <div
              className="admin-filter-pills"
              style={{ padding: "0 1.25rem 0.875rem" }}
              role="group"
              aria-label="Category"
            >
              {CATEGORY_FILTERS.map((c) => {
                const label = c === "all" ? "All" : auditCategoryLabel[c];
                const count = c === "all" ? allEvents.length : categoryCounts[c];
                return (
                  <button
                    key={c}
                    type="button"
                    className={`admin-filter-pill${category === c ? " admin-filter-pill-active" : ""}`}
                    onClick={() => setCategory(c)}
                    aria-pressed={category === c}
                  >
                    {label}
                    <span style={{ opacity: 0.7 }}> ({count})</span>
                  </button>
                );
              })}
            </div>

            {!q.isLoading && allEvents.length > 0 ? (
              <div className="admin-count-strip">
                <span>
                  Showing <strong>{filtered.length}</strong> of <strong>{allEvents.length}</strong>{" "}
                  loaded
                </span>
                {q.hasNextPage ? (
                  <span>More available — load below</span>
                ) : (
                  <span>End of buffer</span>
                )}
              </div>
            ) : null}

            <div className="admin-panel-body">
              {q.isLoading ? (
                <div className="admin-panel-body-padded">
                  <AuditEventTimeline events={[]} selectedId={null} onSelect={() => {}} loading />
                </div>
              ) : q.isError ? (
                <div className="admin-panel-body-padded">
                  <Alert variant="destructive">
                    <AlertTitle>Failed to load audit log</AlertTitle>
                    <AlertDescription>{q.error.message}</AlertDescription>
                  </Alert>
                </div>
              ) : filtered.length === 0 ? (
                <div className="admin-empty-state">
                  <div className="admin-empty-state-icon">
                    <ScrollText style={{ width: 22, height: 22 }} />
                  </div>
                  <p className="admin-inbox-zero-title">No events match</p>
                  <p className="admin-empty-state-text">
                    {allEvents.length === 0
                      ? `No product audit events in ${envLabel(environment)} yet.`
                      : "Try clearing filters or loading more history."}
                  </p>
                  {(search.length > 0 || category !== "all") && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        setSearch("");
                        setCategory("all");
                      }}
                    >
                      Clear filters
                    </Button>
                  )}
                </div>
              ) : (
                <AuditEventTimeline
                  events={filtered}
                  selectedId={selectedEvent?.id ?? null}
                  onSelect={setSelectedId}
                />
              )}
            </div>

            {q.hasNextPage ? (
              <div className="admin-audit-load-more">
                <Button
                  variant="secondary"
                  disabled={q.isFetchingNextPage}
                  onClick={() => void q.fetchNextPage()}
                >
                  {q.isFetchingNextPage ? (
                    <Spinner style={{ width: 16, height: 16, marginRight: 8 }} />
                  ) : (
                    <FileSearch style={{ width: 16, height: 16, marginRight: 8 }} />
                  )}
                  Load more events
                </Button>
              </div>
            ) : allEvents.length > 0 ? (
              <p className="admin-audit-end-marker">
                {allEvents.length} events loaded · append-only stream
              </p>
            ) : null}
          </section>
        </div>

        <AuditEventInspector event={selectedEvent} />
      </div>
    </div>
  );
};
